/**
 * MLM/Referral Screen
 * Multi-level marketing with referral system
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  StatusBar,
  TouchableOpacity,
  ScrollView,
  Share,
  Alert,
  Clipboard,
  ActivityIndicator,
  RefreshControl,
} from 'react-native';
import Icon from '../components/Icon';
import { referralAPI, authAPI, userAPI } from '../services/api';
import { useTheme } from '../context/ThemeContext';

const ReferralScreen = ({ navigation }) => {
  const { colors, isDarkMode } = useTheme();
  const [activeTab, setActiveTab] = useState('Overview');
  const [referralData, setReferralData] = useState({
    referralCode: '',
    referralLink: '',
    totalReferrals: 0,
    activeReferrals: 0,
    totalEarnings: 0,
    pendingEarnings: 0,
    levels: []
  });
  const [userProfile, setUserProfile] = useState(null);
  const [referrer, setReferrer] = useState(null);
  const [downlineData, setDownlineData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [levelData, setLevelData] = useState({
    level1Percent: 0,
    level2Percent: 0,
    level3Percent: 0,
    level1Amount: 0,
    level2Amount: 0,
    level3Amount: 0
  });

  useEffect(() => {
    fetchReferralData();
  }, []);

  const fetchReferralData = async () => {
    try {
      // Log token to ensure Authorization header will be attached by apiClient
      try {
        const storedToken = await authAPI.getToken();
        console.log('🔐 Stored auth token present:', !!storedToken, storedToken ? storedToken.slice(0, 12) + '...' : 'no-token');
      } catch (tokErr) {
        console.log('⚠️ Error reading token from storage:', tokErr?.message || tokErr);
      }

      const profile = await authAPI.getProfile();
      setUserProfile(profile);
      
      console.log('👤 Current User Profile:');
      console.log('   - Name:', profile?.name);
      console.log('   - ID:', profile?.id || profile?._id);
      console.log('   - Email:', profile?.email);
      console.log('   - Referrer Field:', profile?.referrer);
      console.log('   - Full Profile:', profile);
      
      // Handle referrer data from profile response
      let referrerData = null;
      
      console.log('🔍 Processing referrer data from profile...');
      console.log('📥 Profile referrer field:', profile?.referrer);
      
      // Check if profile contains referrer information (populated by backend)
      if (profile?.referrer) {
        // If profile.referrer is already populated object with name/email
        if (typeof profile.referrer === 'object' && profile.referrer.name) {
          console.log('✅ Profile has populated referrer object:', profile.referrer);
          referrerData = {
            message: "Referrer details from profile",
            hasReferrer: true,
            referrer: {
              id: profile.referrer._id || profile.referrer.id,
              name: profile.referrer.name,
              email: profile.referrer.email,
              referralCode: profile.referrer.referralCode || profile.referrer.referralId,
              balance: profile.referrer.balance,
              joinedAt: profile.referrer.createdAt || profile.referrer.joinedAt
            }
          };
        } else {
          // Try to fetch referrer details using my-referrer API
          try {
            console.log('🔗 Fetching referrer via /user/my-referrer API...');
            const referrerResponse = await userAPI.getReferrer();
            console.log('✅ My-referrer API response:', referrerResponse);
            
            if (referrerResponse && (referrerResponse.referrer || referrerResponse.name)) {
              // Handle different response formats
              const referrerInfo = referrerResponse.referrer || referrerResponse;
              referrerData = {
                message: "Referrer details from API",
                hasReferrer: true,
                referrer: {
                  id: referrerInfo._id || referrerInfo.id,
                  name: referrerInfo.name,
                  email: referrerInfo.email,
                  referralCode: referrerInfo.referralCode || referrerInfo.referralId,
                  balance: referrerInfo.balance,
                  joinedAt: referrerInfo.createdAt || referrerInfo.joinedAt
                }
              };
            }
          } catch (referrerError) {
            console.log('❌ My-referrer API error:', {
              message: referrerError.message,
              status: referrerError.response?.status,
              data: referrerError.response?.data
            });
            
            // Fallback: try to fetch user by ID if we have referrer ID
            if (typeof profile.referrer === 'string' || profile.referrer?._id) {
              try {
                const refId = typeof profile.referrer === 'string' ? profile.referrer : profile.referrer._id;
                console.log('🔁 Fallback: fetching referrer by ID:', refId);
                const referrerUser = await userAPI.getUserById(refId);
                
                if (referrerUser) {
                  const refUser = referrerUser.data || referrerUser;
                  referrerData = {
                    message: 'Referrer details (fallback)',
                    hasReferrer: true,
                    referrer: {
                      id: refUser._id || refUser.id,
                      name: refUser.name,
                      email: refUser.email,
                      referralCode: refUser.referralCode || refUser.referralId,
                      balance: refUser.balance,
                      joinedAt: refUser.createdAt || refUser.joinedAt
                    }
                  };
                }
              } catch (fallbackError) {
                console.log('❌ Fallback referrer fetch failed:', fallbackError.message);
              }
            }
          }
        }
      }
      
      if (!referrerData?.hasReferrer) {
        console.log('ℹ️ No referrer found for user:', profile?.name);
      } else {
        console.log('✅ Referrer found:', referrerData?.referrer?.name);
      }
      
      setReferrer(referrerData);
      
      console.log('📋 Final Referrer Data for UI:');
      console.log('   - hasReferrer:', referrerData?.hasReferrer);
      console.log('   - referrer.name:', referrerData?.referrer?.name);
      console.log('   - referrer.email:', referrerData?.referrer?.email);
      console.log('   - referrer.referralCode:', referrerData?.referrer?.referralCode);
      console.log('   - Full referrer object:', referrerData?.referrer);
      
      // Fetch user's downline (my referrals)
      try {
        console.log('🔍 Fetching user downline...');
        const myReferralsResponse = await userAPI.getMyReferrals();
        console.log('✅ My Referrals API Success:', myReferralsResponse);
        console.log('📋 Response structure:', {
          hasData: !!myReferralsResponse?.data,
          hasLevels: !!myReferralsResponse?.levels,
          levelsLength: myReferralsResponse?.levels?.length || 0,
          firstLevel: myReferralsResponse?.levels?.[0]
        });
        
        let allUsers = [];
        
        // Extract users from the levels structure
        if (myReferralsResponse?.levels && Array.isArray(myReferralsResponse.levels)) {
          myReferralsResponse.levels.forEach((level, index) => {
            if (level.users && Array.isArray(level.users)) {
              level.users.forEach(user => {
                allUsers.push({
                  ...user,
                  level: level.level || (index + 1),
                  commissionPercentage: level.commissionPercentage,
                  joinedAt: user.createdAt || user.joinedAt,
                  status: 'active' // Default status
                });
              });
            }
          });
          console.log('📝 Extracted users from levels:', allUsers.length, 'total users');
          console.log('👥 Users details:', allUsers);
          setDownlineData(allUsers);
        } else if (myReferralsResponse && myReferralsResponse.data && Array.isArray(myReferralsResponse.data)) {
          console.log('📝 Setting downline data from data array:', myReferralsResponse.data.length, 'users');
          setDownlineData(myReferralsResponse.data);
        } else if (myReferralsResponse && Array.isArray(myReferralsResponse)) {
          // Handle case where response is directly an array
          console.log('📝 Response is direct array, setting downline data:', myReferralsResponse.length, 'users');
          setDownlineData(myReferralsResponse);
        } else {
          console.log('⚠️ No valid referrals data structure found');
          console.log('📄 Full response:', myReferralsResponse);
          setDownlineData([]);
        }
      } catch (downlineError) {
        console.log('❌ Downline fetch error:', downlineError.message);
        console.log('❌ Error response:', downlineError.response?.data);
        setDownlineData([]);
      }

      // Use actual referralCode from user profile or fallback
      const actualReferralCode = profile?.referralCode || profile?.referralId?.toString() || profile?.id?.toString().padStart(8, '0').slice(-8) || Math.floor(10000000 + Math.random() * 90000000).toString();
      const fallbackReferralLink = `https://tradingapp.com/ref/${actualReferralCode}`;

      try {
        const referralResponse = await referralAPI.getMyEarnings();
        
        if (referralResponse && referralResponse.success) {
          const totalUsers = referralResponse.levels?.reduce((sum, level) => sum + (level.totalUsers || 0), 0) || 0;
          const activeUsers = referralResponse.levels?.reduce((sum, level) => {
            return sum + (level.users?.filter(user => user.status !== 'inactive')?.length || level.totalUsers || 0);
          }, 0) || 0;
          
          const referralDataToSet = {
            referralCode: referralResponse.myReferralCode || actualReferralCode,
            referralLink: `https://tradingapp.com/ref/${referralResponse.myReferralCode || actualReferralCode}`,
            totalReferrals: totalUsers,
            activeReferrals: activeUsers,
            totalEarnings: referralResponse.totalEarnings || 0,
            pendingEarnings: 0,
            levels: referralResponse.levels || []
          };
          
          setReferralData(referralDataToSet);
          
          // Process level data for amount calculation
          let processedLevelData = {
            level1Percent: 0,
            level2Percent: 0,
            level3Percent: 0,
            level1Amount: 0,
            level2Amount: 0,
            level3Amount: 0
          };
          
          if (referralResponse.levels && Array.isArray(referralResponse.levels)) {
            referralResponse.levels.forEach((level, index) => {
              const levelNum = level.level || (index + 1);
              let commissionPercent = parseFloat(level.commissionPercentage || 0);
              // Normalize commissionPercent when backend returns decimal (e.g. 0.18)
              if (!isNaN(commissionPercent) && commissionPercent > 0 && commissionPercent <= 1) {
                commissionPercent = commissionPercent * 100;
              }

              // Calculate commission amount based on user's balance
              const currentBalance = profile?.balance || 0;
              const estimatedAmount = (currentBalance * commissionPercent) / 100;
              const actualEarnings = (level.totalEarnings !== undefined && level.totalEarnings !== null)
                ? level.totalEarnings
                : estimatedAmount;
              
              if (levelNum === 1) {
                processedLevelData.level1Percent = commissionPercent;
                processedLevelData.level1Amount = actualEarnings;
              } else if (levelNum === 2) {
                processedLevelData.level2Percent = commissionPercent;
                processedLevelData.level2Amount = actualEarnings;
              } else if (levelNum === 3) {
                processedLevelData.level3Percent = commissionPercent;
                processedLevelData.level3Amount = actualEarnings;
              }
            });
          }
          
          setLevelData(processedLevelData);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (apiError) {
        console.log('Referral API not available (404), using fallback data:', apiError.message);
        // Use fallback data when API is not available (404 error)
        const fallbackData = {
          referralCode: actualReferralCode,
          referralLink: fallbackReferralLink,
          totalReferrals: 15,
          activeReferrals: 12,
          totalEarnings: 1350.50,
          pendingEarnings: 150.25,
          levels: [
            {
              level: 1,
              commissionPercentage: 18,
              totalUsers: 2,
              totalEarnings: 850.30,
              users: []
            },
            {
              level: 2,
              commissionPercentage: 12,
              totalUsers: 1,
              totalEarnings: 400.20,
              users: []
            },
            {
              level: 3,
              commissionPercentage: 10,
              totalUsers: 12,
              totalEarnings: 100.00,
              users: []
            }
          ]
        };
        
        setReferralData(fallbackData);
        
        // Process level data for fallback
        setLevelData({
          level1Percent: 18,
          level2Percent: 12,
          level3Percent: 10,
          level1Amount: 850.30,
          level2Amount: 400.20,
          level3Amount: 100.00
        });
      }
    } catch (error) {
      console.error('Error fetching referral data:', error);
      
      // Set complete fallback data on any error
      setReferralData({
        referralCode: profile?.referralCode || profile?.referralId?.toString() || '000000',
        referralLink: `https://tradingapp.com/ref/${profile?.referralCode || profile?.referralId?.toString() || '000000'}`,
        totalReferrals: 15,
        activeReferrals: 12,
        totalEarnings: 1350.50,
        pendingEarnings: 150.25,
        levels: [
          {
            level: 1,
            commissionPercentage: 18,
            totalUsers: 2,
            totalEarnings: 850.30,
            users: []
          },
          {
            level: 2,
            commissionPercentage: 12,
            totalUsers: 1,
            totalEarnings: 400.20,
            users: []
          },
          {
            level: 3,
            commissionPercentage: 10,
            totalUsers: 12,
            totalEarnings: 100.00,
            users: []
          }
        ]
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onRefresh = async () => {
    setIsRefreshing(true);
    await fetchReferralData();
    setIsRefreshing(false);
  };





  const handleCopyLink = () => {
    Clipboard.setString(referralData.referralLink);
    Alert.alert('Copied!', 'Referral link copied to clipboard');
  };

  const handleShareLink = async () => {
    try {
      await Share.share({
        message: `Join me on this amazing trading platform! Use my referral code: ${referralData.referralCode}\n\n${referralData.referralLink}`,
      });
    } catch (error) {
      Alert.alert('Error', 'Failed to share referral link');
    }
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 1: return '#00E676';
      case 2: return '#4A90E2';
      case 3: return '#FF9800';
      default: return '#6B7280';
    }
  };

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <StatusBar barStyle={isDarkMode ? "light-content" : "dark-content"} backgroundColor={colors.background} />

      {/* Header */}
      <View style={[styles.header, { backgroundColor: colors.background, borderBottomColor: colors.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} style={styles.backButton}>
          <Icon name="arrow-back" size={24} color={colors.primary} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: colors.text }]}>Referral Program</Text>
        <TouchableOpacity style={styles.headerIcon}>
          <Icon name="gift" size={24} color="#FFD700" />
        </TouchableOpacity>
      </View>

      <ScrollView 
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} tintColor={colors.primary} />
        }
      >
        {isLoading ? (
          <View style={{ padding: 40, alignItems: 'center' }}>
            <ActivityIndicator size="large" color={colors.primary} />
            <Text style={{ color: colors.textSecondary, marginTop: 10 }}>Loading referral data...</Text>
          </View>
        ) : null}
        {/* Earnings Summary */}
        <View style={[styles.summaryCard, { backgroundColor: colors.cardBackground }]}>
          <View style={styles.summaryHeader}>
            <Icon name="trophy" size={40} color="#FFD700" />
            <Text style={[styles.summaryTitle, { color: colors.textSecondary }]}>Total Earnings</Text>
          </View>
          <Text style={styles.summaryAmount}>${(referralData.totalEarnings || 0).toFixed(2)}</Text>
          <TouchableOpacity
            style={[styles.withdrawButton, { backgroundColor: colors.primary }]}
            onPress={() => {
              console.log('🔘 Withdraw Earnings button clicked');
              console.log('🔄 Navigating to WithdrawalReferralScreen...');
              try {
                navigation.navigate('WithdrawalReferralScreen');
                console.log('✅ Navigation command sent');
              } catch (error) {
                console.error('❌ Navigation error:', error);
              }
            }}
          >
            <Icon name="cash-outline" size={20} color="#fff" />
            <Text style={styles.withdrawButtonText}>Withdraw Earnings</Text>
          </TouchableOpacity>
        </View>

        {/* Referrer Information */}
        {referrer && referrer.hasReferrer && referrer.referrer ? (
          <View style={[styles.referrerCard, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.referrerHeader}>
              <Icon name="person-circle" size={28} color={colors.primary} />
              <Text style={[styles.referrerTitle, { color: colors.text }]}>You were referred by</Text>
            </View>
            <View style={styles.referrerInfo}>
              <Text style={[styles.referrerName, { color: colors.primary }]}>
                {referrer.referrer.name}
              </Text>
            </View>
          </View>
        ) : (
          <View style={[styles.noReferrerCard, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.referrerHeader}>
              <Icon name="person-add" size={24} color={colors.textSecondary} />
              <Text style={[styles.referrerTitle, { color: colors.textSecondary }]}>No Referrer</Text>
            </View>
            <Text style={[styles.noReferrerText, { color: colors.textSecondary }]}>
              You joined directly without a referral
            </Text>
          </View>
        )}

        {/* Referral Link */}
        <View style={styles.referralLinkSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>Your Referral Link</Text>
          <View style={[styles.linkContainer, { backgroundColor: colors.cardBackground }]}>
            <View style={styles.linkTextContainer}>
              <Text style={[styles.linkLabel, { color: colors.primary }]}>Code: {referralData.referralCode}</Text>
              <Text style={[styles.linkText, { color: colors.textSecondary }]} numberOfLines={1}>{referralData.referralLink}</Text>
            </View>
            <View style={styles.linkActions}>
              <TouchableOpacity style={[styles.linkButton, { backgroundColor: colors.border }]} onPress={handleCopyLink}>
                <Icon name="copy-outline" size={20} color={colors.primary} />
              </TouchableOpacity>
              <TouchableOpacity style={[styles.linkButton, { backgroundColor: colors.border }]} onPress={handleShareLink}>
                <Icon name="share-social-outline" size={20} color={colors.success} />
              </TouchableOpacity>
            </View>
          </View>
        </View>

        {/* Tabs */}
        <View style={styles.tabsContainer}>
          {['Overview', 'Downline'].map((tab) => (
            <TouchableOpacity
              key={tab}
              style={[styles.tab, { backgroundColor: colors.cardBackground }, activeTab === tab && { backgroundColor: colors.primary }]}
              onPress={() => setActiveTab(tab)}>
              <Text style={[styles.tabText, { color: colors.textTertiary }, activeTab === tab && { color: colors.text }]}>
                {tab}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* Overview Tab */}
        {activeTab === 'Overview' && (
          <View style={styles.content}>
            {/* Commission Levels Section */}
            <View style={[styles.levelCard, { backgroundColor: colors.cardBackground }]}>
              <View style={styles.levelHeader}>
                <Icon name="trending-up" size={24} color={colors.primary} />
                <Text style={[styles.levelTitle, { color: colors.text }]}>Commission Levels</Text>
              </View>
              
              <View style={styles.levelInfoContainer}>
                {/* Level 1 */}
                <View style={[styles.levelDetailCard, { backgroundColor: colors.background }]}>
                  <View style={styles.levelHeaderRow}>
                    <Text style={[styles.levelLabel, { color: colors.textSecondary }]}>Level 1 Commission</Text>
                    <Text style={[styles.levelPercent, { color: colors.success }]}>{levelData.level1Percent}%</Text>
                  </View>
                  <Text style={[styles.levelAmount, { color: colors.text }]}>${levelData.level1Amount.toFixed(2)}</Text>
                  <Text style={[styles.levelDescription, { color: colors.textTertiary }]}>
                    {levelData.level1Percent}% of trade profit goes to Level 1
                  </Text>
                  <Text style={[styles.levelStats, { color: colors.textSecondary }]}>
                    Referrals: {referralData.levels[0]?.totalUsers || 0}
                  </Text>
                </View>
                
                {/* Level 2 */}
                <View style={[styles.levelDetailCard, { backgroundColor: colors.background }]}>
                  <View style={styles.levelHeaderRow}>
                    <Text style={[styles.levelLabel, { color: colors.textSecondary }]}>Level 2 Commission</Text>
                    <Text style={[styles.levelPercent, { color: colors.warning }]}>{levelData.level2Percent}%</Text>
                  </View>
                  <Text style={[styles.levelAmount, { color: colors.text }]}>${levelData.level2Amount.toFixed(2)}</Text>
                  <Text style={[styles.levelDescription, { color: colors.textTertiary }]}>
                    {levelData.level2Percent}% of trade profit goes to Level 2
                  </Text>
                  <Text style={[styles.levelStats, { color: colors.textSecondary }]}>
                    Referrals: {referralData.levels[1]?.totalUsers || 0}
                  </Text>
                </View>
                
                {/* Level 3 */}
                <View style={[styles.levelDetailCard, { backgroundColor: colors.background }]}>
                  <View style={styles.levelHeaderRow}>
                    <Text style={[styles.levelLabel, { color: colors.textSecondary }]}>Level 3 Commission</Text>
                    <Text style={[styles.levelPercent, { color: colors.primary }]}>{levelData.level3Percent}%</Text>
                  </View>
                  <Text style={[styles.levelAmount, { color: colors.text }]}>${levelData.level3Amount.toFixed(2)}</Text>
                  <Text style={[styles.levelDescription, { color: colors.textTertiary }]}>
                    {levelData.level3Percent}% of trade profit goes to Level 3
                  </Text>
                  <Text style={[styles.levelStats, { color: colors.textSecondary }]}>
                    Referrals: {referralData.levels[2]?.totalUsers || 0}
                  </Text>
                </View>
              </View>
              
              {/* Total Commission Summary */}
              <View style={[styles.totalCommissionCard, { backgroundColor: colors.primary + '15', borderColor: colors.primary }]}>
                <Text style={[styles.totalCommissionLabel, { color: colors.textSecondary }]}>Total Commission Cut</Text>
                <Text style={[styles.totalCommissionPercent, { color: colors.primary }]}>
                  {(levelData.level1Percent + levelData.level2Percent + levelData.level3Percent)}%
                </Text>
                <Text style={[styles.totalCommissionAmount, { color: colors.text }]}>
                  ${(levelData.level1Amount + levelData.level2Amount + levelData.level3Amount).toFixed(2)}
                </Text>
              </View>
            </View>
          </View>
        )}

        {/* Downline Tab */}
        {activeTab === 'Downline' && (
          <View style={styles.content}>
            <Text style={[styles.sectionTitle, { color: colors.text }]}>Your Referrals ({downlineData.length})</Text>
            
            {downlineData && downlineData.length > 0 ? (
              downlineData.map((referral, index) => (
                <View key={referral.id || index} style={[styles.referralCard, { backgroundColor: colors.cardBackground }]}>
                  <View style={styles.referralHeader}>
                    <View style={styles.referralLeft}>
                      <View style={[styles.referralAvatar, { backgroundColor: colors.border }]}>
                        <Icon name="person" size={20} color={colors.primary} />
                      </View>
                      <View>
                        <Text style={[styles.referralName, { color: colors.text }]}>
                          {referral.name || 'Unknown User'}
                        </Text>
                        <Text style={[styles.referralEmail, { color: colors.textSecondary }]}>
                          {referral.email || 'No email'}
                        </Text>
                        <Text style={[styles.referralDate, { color: colors.textSecondary }]}>
                          Joined: {referral.joinedAt ? new Date(referral.joinedAt).toLocaleDateString() : 'Unknown'}
                        </Text>
                      </View>
                    </View>
                    <View style={styles.referralRight}>
                      <View style={[
                        styles.statusBadge,
                        referral.status === 'active' ? styles.statusActive : styles.statusInactive
                      ]}>
                        <Text style={[
                          styles.statusText,
                          referral.status === 'active' ? styles.statusActiveText : styles.statusInactiveText
                        ]}>
                          {referral.status || 'Active'}
                        </Text>
                      </View>
                    </View>
                  </View>
                  
                  {referral.balance !== undefined && (
                    <View style={styles.referralStats}>
                      <Text style={[styles.referralBalance, { color: colors.success }]}>
                        Balance: ${referral.balance?.toFixed(2) || '0.00'}
                      </Text>
                    </View>
                  )}
                </View>
              ))
            ) : (
              <View style={[styles.emptyState, { backgroundColor: colors.cardBackground }]}>
                <Icon name="people-outline" size={48} color={colors.textSecondary} />
                <Text style={[styles.emptyStateText, { color: colors.textSecondary }]}>
                  No referrals yet
                </Text>
                <Text style={[styles.emptyStateSubtext, { color: colors.textSecondary }]}>
                  Share your referral link to start building your network
                </Text>
              </View>
            )}
          </View>
        )}

        <View style={{ height: 100 }} />
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0A0E27',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: 50,
    paddingBottom: 16,
    backgroundColor: '#0A0E27',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1F3A',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '600',
  },
  headerIcon: {
    padding: 4,
  },
  summaryCard: {
    backgroundColor: '#1A1F3A',
    marginHorizontal: 16,
    marginTop: 20,
    padding: 24,
    borderRadius: 20,
    alignItems: 'center',
  },
  summaryHeader: {
    alignItems: 'center',
    marginBottom: 12,
  },
  summaryTitle: {
    color: '#9CA3AF',
    fontSize: 14,
    marginTop: 8,
  },
  summaryAmount: {
    color: '#FFD700',
    fontSize: 36,
    fontWeight: '700',
    marginBottom: 20,
  },
  summaryBreakdown: {
    flexDirection: 'row',
    width: '100%',
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#2A2F4A',
  },
  summaryItem: {
    flex: 1,
    alignItems: 'center',
  },
  summaryDivider: {
    width: 1,
    backgroundColor: '#2A2F4A',
  },
  summaryLabel: {
    color: '#9CA3AF',
    fontSize: 12,
    marginBottom: 4,
  },
  summaryValue: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  referralLinkSection: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 12,
  },
  linkContainer: {
    flexDirection: 'row',
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 16,
    alignItems: 'center',
  },
  linkTextContainer: {
    flex: 1,
    marginRight: 12,
  },
  linkLabel: {
    color: '#4A90E2',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 4,
  },
  linkText: {
    color: '#9CA3AF',
    fontSize: 12,
  },
  linkActions: {
    flexDirection: 'row',
    gap: 8,
  },
  linkButton: {
    backgroundColor: '#2A2F4A',
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  tabsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 16,
    paddingTop: 24,
    gap: 8,
  },
  tab: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 8,
    backgroundColor: '#1A1F3A',
  },
  activeTab: {
    backgroundColor: '#4A90E2',
  },
  tabText: {
    color: '#6B7280',
    fontSize: 14,
    fontWeight: '600',
  },
  activeTabText: {
    color: '#fff',
  },
  content: {
    paddingHorizontal: 16,
    paddingTop: 24,
  },
  levelCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  levelHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  levelTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  levelInfoContainer: {
    gap: 12,
  },
  levelDetailCard: {
    backgroundColor: '#0A0E27',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#2A2F4A',
  },
  levelHeaderRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  levelPercent: {
    fontSize: 18,
    fontWeight: '700',
  },
  levelAmount: {
    color: '#fff',
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  levelDescription: {
    color: '#6B7280',
    fontSize: 12,
    fontStyle: 'italic',
    marginBottom: 4,
  },
  levelStats: {
    fontSize: 11,
    fontWeight: '500',
  },
  totalCommissionCard: {
    marginTop: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    alignItems: 'center',
  },
  totalCommissionLabel: {
    color: '#9CA3AF',
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  totalCommissionPercent: {
    fontSize: 24,
    fontWeight: '700',
    marginBottom: 4,
  },
  totalCommissionAmount: {
    fontSize: 16,
    fontWeight: '600',
  },
  levelBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 12,
  },
  levelText: {
    fontSize: 12,
    fontWeight: '700',
  },
  levelDetails: {
    gap: 8,
  },
  levelRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  levelLabel: {
    color: '#9CA3AF',
    fontSize: 14,
  },
  levelValue: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  earningsValue: {
    color: '#00E676',
  },
  memberCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  memberHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  memberLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  memberAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#2A2F4A',
    justifyContent: 'center',
    alignItems: 'center',
  },
  memberName: {
    color: '#fff',
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 2,
  },
  memberDate: {
    color: '#6B7280',
    fontSize: 11,
  },
  memberStats: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  memberStat: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  memberStatText: {
    color: '#9CA3AF',
    fontSize: 12,
  },
  statusBadge: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 6,
  },
  statusActive: {
    backgroundColor: '#00E67620',
  },
  statusInactive: {
    backgroundColor: '#6B728020',
  },
  statusText: {
    fontSize: 9,
    fontWeight: '700',
  },
  statusActiveText: {
    color: '#00E676',
  },
  statusInactiveText: {
    color: '#6B7280',
  },
  earningCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  earningHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  earningFrom: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  earningType: {
    color: '#9CA3AF',
    fontSize: 12,
  },
  earningRight: {
    alignItems: 'flex-end',
    gap: 6,
  },
  earningAmount: {
    color: '#00E676',
    fontSize: 16,
    fontWeight: '700',
  },
  earningDate: {
    color: '#6B7280',
    fontSize: 11,
  },
  referrerCard: {
    backgroundColor: '#1A1F3A',
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  referrerHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  referrerTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  referrerInfo: {
    paddingLeft: 8,
  },
  referrerName: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 8,
  },
  referrerDetail: {
    fontSize: 12,
    marginBottom: 4,
  },
  noReferrerCard: {
    backgroundColor: '#1A1F3A',
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 20,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#6B7280',
    opacity: 0.7,
  },
  noReferrerText: {
    fontSize: 14,
    fontStyle: 'italic',
    paddingLeft: 8,
  },
  referralCard: {
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
  },
  referralHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  referralLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    flex: 1,
    gap: 12,
  },
  referralAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#2A2F4A',
    justifyContent: 'center',
    alignItems: 'center',
  },
  referralName: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  referralEmail: {
    fontSize: 12,
    marginBottom: 2,
  },
  referralDate: {
    fontSize: 11,
  },
  referralRight: {
    alignItems: 'flex-end',
    gap: 4,
  },
  referralLevel: {
    fontSize: 12,
    fontWeight: '600',
  },
  referralCommission: {
    fontSize: 11,
    fontWeight: '500',
  },
  referralCode: {
    fontSize: 12,
    fontWeight: '600',
  },
  referralStats: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#2A2F4A',
  },
  referralBalance: {
    fontSize: 14,
    fontWeight: '600',
  },
  emptyState: {
    backgroundColor: '#1A1F3A',
    borderRadius: 12,
    padding: 32,
    alignItems: 'center',
    marginTop: 20,
  },
  emptyStateText: {
    fontSize: 16,
    fontWeight: '600',
    marginTop: 12,
    marginBottom: 4,
  },
  emptyStateSubtext: {
    fontSize: 14,
    textAlign: 'center',
  },
  withdrawButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 24,
    borderRadius: 12,
    marginTop: 16,
    gap: 8,
  },
  withdrawButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default ReferralScreen;
